// src/admin/components/reports/ReportDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  RiBarChartBoxLine,
  RiLineChartLine,
  RiBuildingLine,
  RiUserLine,
  RiMoneyDollarCircleLine,
  RiCalendarLine,
  RiFileChartLine,
  RiAlertLine,
  RiDownloadLine,
  RiRefreshLine,
  RiEyeLine,
  RiPrinterLine
} from 'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import { getAnnualSummary, getCurrentYearStartEnd, formatCurrency } from '../../../models/reportModel';
import RevenueChart from './charts/RevenueChart';
import OccupancyWidget from './widgets/OccupancyWidget';
import RecentPaymentsWidget from './widgets/RecentPaymentsWidget';
import OverdueTenantsWidget from './widgets/OverdueTenantsWidget';

const ReportDashboard = () => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();

  // Check if the current language is RTL
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [annualData, setAnnualData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getAnnualSummary(year);
        setAnnualData(response);
      } catch (err) {
        console.error('Error fetching report data:', err);
        setError(err.message || t('failed_to_load_report_data') || 'Failed to load report data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [year, t]);

  const handleYearChange = (e) => {
    setYear(parseInt(e.target.value));
  };

  const { startDate, endDate } = getCurrentYearStartEnd();

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
            <RiBarChartBoxLine className={`${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
            {t('reports_dashboard')}
          </h1>
          <p className={`mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
            {t('overview_of_financial_performance')}
          </p>
        </div>

        <div className={`flex items-center ${isRTL ? 'space-x-3 space-x-reverse' : 'space-x-3'}`}>
          <select
            value={year}
            onChange={handleYearChange}
            className={`px-3 py-2 rounded-lg ${isDarkMode
              ? 'bg-zinc-800 text-white border-gray-600'
              : 'bg-white text-gray-700 border-gray-300'
              } border`}
          >
            {annualData?.availableYears?.map(y => (
              <option key={y} value={y}>{y}</option>
            )) || (
                <option value={year}>{year}</option>
              )}
          </select>

          <Link
            to="/admin/reports/revenue"
            className={`px-4 py-2 rounded-lg ${isDarkMode
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-zinc-500 text-white hover:bg-red-600'
              } transition-colors duration-200 flex items-center`}
          >
            <RiFileChartLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
            {t('view_all_reports')}
          </Link>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-red-500"></div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-zinc-50 border-red-200 text-red-700'
          } border flex items-center`}>
          <RiAlertLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-xl`} />
          {error}
        </div>
      )}

      {/* Dashboard Content */}
      {!loading && !error && annualData && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('total_revenue')}
                  </p>
                  <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(annualData.annualSummary?.total_revenue || 0)}
                  </h3>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('total_payments_count', { count: annualData.annualSummary?.total_payments || 0 })}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'
                  }`}>
                  <RiMoneyDollarCircleLine size={24} />
                </div>
              </div>
            </div>

            <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('total_tenants')}
                  </p>
                  <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {annualData.annualSummary?.tenant_count || 0}
                  </h3>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('new_tenants_this_year', { count: annualData.annualSummary?.new_tenants || 0 })}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-zinc-100 text-red-600'
                  }`}>
                  <RiUserLine size={24} />
                </div>
              </div>
            </div>

            <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('total_properties')}
                  </p>
                  <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {annualData.annualSummary?.total_apartments || 0}
                  </h3>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('total_buildings_count', { count: annualData.annualSummary?.total_buildings || 0 })}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${isDarkMode ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-600'
                  }`}>
                  <RiBuildingLine size={24} />
                </div>
              </div>
            </div>

            <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('total_owners')}
                  </p>
                  <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {annualData.annualSummary?.total_owners || 0}
                  </h3>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('ended_contracts_count', { count: annualData.annualSummary?.ended_contracts || 0 })}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${isDarkMode ? 'bg-orange-900/20 text-orange-400' : 'bg-orange-100 text-orange-600'
                  }`}>
                  <RiUserLine size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* Charts & Widgets Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Chart */}
            <div className={`lg:col-span-2 p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('monthly_revenue_year', { year })}
                </h3>
                <Link
                  to={`/admin/reports/monthly-revenue/${year}`}
                  className={`text-sm flex items-center ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'
                    }`}
                >
                  <RiEyeLine className={`${isRTL ? 'ml-1' : 'mr-1'}`} />
                  {t('view_details')}
                </Link>
              </div>

              <RevenueChart data={annualData.monthlyRevenue || []} isDarkMode={isDarkMode} isRTL={isRTL} />
            </div>

            {/* Occupancy Widget */}
            <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('occupancy_rate')}
                </h3>
                <Link
                  to="/admin/reports/occupancy"
                  className={`text-sm flex items-center ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'
                    }`}
                >
                  <RiEyeLine className={`${isRTL ? 'ml-1' : 'mr-1'}`} />
                  {t('view_details')}
                </Link>
              </div>

              <OccupancyWidget isDarkMode={isDarkMode} isRTL={isRTL} />
            </div>
          </div>

          {/* Second Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Payments */}
            <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('recent_payments')}
                </h3>
                <Link
                  to="/admin/reports/payment-logs"
                  className={`text-sm flex items-center ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'
                    }`}
                >
                  <RiEyeLine className={`${isRTL ? 'ml-1' : 'mr-1'}`} />
                  {t('view_all')}
                </Link>
              </div>

              <RecentPaymentsWidget isDarkMode={isDarkMode} isRTL={isRTL} />
            </div>

            {/* Overdue Tenants */}
            <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('overdue_payments')}
                </h3>
                <Link
                  to="/admin/reports/tenant-payments"
                  className={`text-sm flex items-center ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'
                    }`}
                >
                  <RiEyeLine className={`${isRTL ? 'ml-1' : 'mr-1'}`} />
                  {t('view_all')}
                </Link>
              </div>

              <OverdueTenantsWidget isDarkMode={isDarkMode} isRTL={isRTL} />
            </div>
          </div>

          {/* Quick Reports */}
          <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
            }`}>
            <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('quick_reports')}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <Link
                to={`/admin/reports/revenue?startDate=${startDate}&endDate=${endDate}`}
                className={`p-4 rounded-lg flex items-center ${isDarkMode ? 'bg-zinc-800 hover:bg-gray-650' : 'bg-gray-100 hover:bg-gray-200'
                  } transition-colors duration-200`}
              >
                <div className={`p-3 rounded-full ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'
                  }`}>
                  <RiMoneyDollarCircleLine size={20} />
                </div>
                <div>
                  <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('annual_revenue')}
                  </h4>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('yearly_breakdown')}
                  </p>
                </div>
              </Link>

              <Link
                to="/admin/reports/building-revenue"
                className={`p-4 rounded-lg flex items-center ${isDarkMode ? 'bg-zinc-800 hover:bg-gray-650' : 'bg-gray-100 hover:bg-gray-200'
                  } transition-colors duration-200`}
              >
                <div className={`p-3 rounded-full ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-zinc-100 text-red-600'
                  }`}>
                  <RiBuildingLine size={20} />
                </div>
                <div>
                  <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('building_revenue')}
                  </h4>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('revenue_by_building')}
                  </p>
                </div>
              </Link>

              <Link
                to="/admin/reports/owner-revenue"
                className={`p-4 rounded-lg flex items-center ${isDarkMode ? 'bg-zinc-800 hover:bg-gray-650' : 'bg-gray-100 hover:bg-gray-200'
                  } transition-colors duration-200`}
              >
                <div className={`p-3 rounded-full ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-600'
                  }`}>
                  <RiUserLine size={20} />
                </div>
                <div>
                  <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('owner_revenue')}
                  </h4>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('revenue_by_owner')}
                  </p>
                </div>
              </Link>

              <Link
                to="/admin/reports/payment-logs"
                className={`p-4 rounded-lg flex items-center ${isDarkMode ? 'bg-zinc-800 hover:bg-gray-650' : 'bg-gray-100 hover:bg-gray-200'
                  } transition-colors duration-200`}
              >
                <div className={`p-3 rounded-full ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'bg-orange-900/20 text-orange-400' : 'bg-orange-100 text-orange-600'
                  }`}>
                  <RiFileChartLine size={20} />
                </div>
                <div>
                  <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('payment_logs')}
                  </h4>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('detailed_payment_history')}
                  </p>
                </div>
              </Link>

              <Link
                to="/admin/reports/monthly-payments"
                className={`p-4 rounded-lg flex items-center ${isDarkMode ? 'bg-zinc-800 hover:bg-gray-650' : 'bg-gray-100 hover:bg-gray-200'
                  } transition-colors duration-200`}
              >
                <div className={`p-3 rounded-full ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-600'
                  }`}>
                  <RiCalendarLine size={20} />
                </div>
                <div>
                  <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('monthly_payments_report')}
                  </h4>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('monthly_payments_subtitle')}
                  </p>
                </div>
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportDashboard;